import { APP_LOGO, apiUrl } from "consts";
import { CarriageWayEnum, ILightingClassConfig, IProjectConfig, IRoadLayoutConfig } from "types";
import { captureAllHeatmaps, convertImagesToBase64 } from "utils/carbone/svgToBase64";
import imgLuminaireReference from "assets/images/lamp-reference.png";
import imgLampRopeReference from "assets/images/lamp-rope-reference.png";
import { getLuminaireLayoutImage, LuminaireLayoutType } from "utils/images";
import { transformToCarboneData } from "utils/carbone/carboneDataTransformer";
import { GeneratePdfParams } from "hooks/use-generate-pdf";

type Props = {
  roadLayoutConfig: IRoadLayoutConfig;
  lightingClassConfig: ILightingClassConfig;
  selectedResult: string | number;
  projectConfig: IProjectConfig;
  data: any;
  article: any;
  displayLuminance: boolean;
  displayIlluminance: boolean;
  appLang: string;
  luminance: any;
  luminance_summary: any;
  illuminance: any;
  surfaces: any;
  sidewalk_placing: any;
  subVal: any;
  subValResult: any;
  loadperkm: number | null;
  lampset: any;
  family_image: string;
  lvk_png_url?: string;
  lvk_img_url?: string;
  article_number: string;
  var_number: string;
  light_flux: number | null;
  power: number | null;
  delta: number | null;
  s_state: string | null;
  e_state_left: string;
  e_state_right: string;
  lph: number | null;
  overhang: number | null;
  pos?: string; // Assuming pos is a string, adjust if necessary
  tilt: number | null;
  maintenanceFactor: number | null;
  roadwayConfig: any;
  resultPageRef: React.RefObject<HTMLDivElement>;
  ulor?: string;
  n_lums?: number;
  f_ti?: number;
  pdfFileName: string;
  t: (key: string) => string;
  generatePdf: (params: GeneratePdfParams) => Promise<void>;
};

// Handler for Carbone PDF generation
export const printCarbonePdf = async ({
  roadLayoutConfig,
  family_image,
  luminance,
  lightingClassConfig,
  selectedResult,
  projectConfig,
  data,
  article,
  displayLuminance,
  displayIlluminance,
  appLang,
  luminance_summary,
  illuminance,
  surfaces,
  sidewalk_placing,
  subVal,
  subValResult,
  loadperkm,
  lampset,
  lvk_png_url,
  lvk_img_url,
  article_number,
  var_number,
  light_flux,
  power,
  delta,
  s_state,
  e_state_left,
  e_state_right,
  lph,
  overhang,
  pos,
  tilt,
  maintenanceFactor,
  roadwayConfig,
  resultPageRef,
  ulor,
  n_lums,
  f_ti,
  t,
  pdfFileName,
  generatePdf,
}: Props) => {
  try {
    const isSingle = roadLayoutConfig?.carriageWay === "SingleCarriageway";
    // Generate layout image URL
    const svgPath = getLuminaireLayoutImage(isSingle ? "single" : "double", pos as LuminaireLayoutType, roadLayoutConfig);

    // Convert images to base64
    const base64Images = await convertImagesToBase64({
      logo: APP_LOGO,
      layout_image: svgPath ? svgPath : undefined,
      luminaire_ref_image: imgLuminaireReference,
      luminaire_ref_rope_image: imgLampRopeReference,
      family_image: family_image,
      // @ts-ignore
      lvk_image: lvk_img_url ? `${apiUrl}${lvk_img_url}` : lvk_png_url,
    });

    // Capture heatmaps from the DOM
    const luminanceKeys = luminance ? Object.keys(luminance).map((key) => key) : [];
    const heatmaps = await captureAllHeatmaps(
      luminanceKeys,
      roadLayoutConfig?.borderAreaLeft || false,
      roadLayoutConfig?.borderAreaRight || false,
      lightingClassConfig?.borderAreaLeftClasses,
      lightingClassConfig?.borderAreaRightClasses
    );

    const carboneData = transformToCarboneData(
      {
        selectedResult: selectedResult?.toString(),
        projectConfig,
        project: projectConfig,
        data,
        article,
        displayLuminance,
        displayIlluminance,
        appLang,
        luminance,
        luminance_summary,
        illuminance,
        surfaces,
        sidewalk_placing,
        subVal,
        subValResult,
        loadperkm: loadperkm?.toString() || "",
        lampset,
        family_image,
        lvk_png_url: lvk_png_url || "",
        lvk_img_url: lvk_img_url || "",
        article_number,
        var_number,
        light_flux: light_flux?.toString() || "",
        power: power?.toString() || "",
        delta: delta?.toString() || "",
        // @ts-ignore
        s_state,
        e_state_left,
        e_state_right,
        // @ts-ignore
        lightingClassConfig,
        lph: lph?.toString() || "",
        overhang: overhang?.toString() || "",
        pos,
        // @ts-ignore
        s_state,
        tilt: tilt?.toString() || "",
        // luminaire_ref,
        // luminaire_ref_rope,
        maintenanceFactor: maintenanceFactor?.toString() || "",
        roadLayoutConfig,
        roadwayConfig,
        // luminaireLayoutHelpFunction,
        // prefix,
        // rootUrl,
        t,
        resultPageRef,
        // @ts-ignore
        ulor,
        // @ts-ignore
        n_lums,
        // @ts-ignore
        f_ti,
      },
      t,
      undefined, // _rootUrl (deprecated)
      heatmaps,
      appLang,
      pdfFileName,
      {
        logo_base64: base64Images.logo,
        layout_image_base64: base64Images.layout_image,
        luminaire_ref_image_base64: base64Images.luminaire_ref_image,
        luminaire_ref_rope_image_base64: base64Images.luminaire_ref_rope_image,
        family_image_base64: base64Images.family_image,
        lvk_image_base64: base64Images.lvk_image,
      }
    );

    await generatePdf({
      template: "anis-temp",
      data: carboneData,
    });
  } catch (err) {
    console.error("Error generating Carbone PDF:", err);
  }
};
